/**
 * Bestellvorschlag.
 *
 * Zeigt alle Artikel unter dem Meldebestand, gruppiert nach Hauptlieferant.
 * Aus einer Gruppe entsteht mit einem Klick eine Bestellung im Entwurf.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { formatPrice, formatQuantity, today } from '@erp/shared';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import { Alert, LoadingBlock } from '../components/ui';

type Suggestion = {
  id: string;
  number: string;
  name: string;
  unit: string;
  stockQuantity: number;
  minStock: number;
  suggestedQuantity: number;
  purchasePrice: number;
  leadTimeDays: number;
  supplierItemNumber: string | null;
};

type Group = {
  supplierId: string | null;
  supplierName: string | null;
  items: Suggestion[];
};

export function ReorderPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();

  const [busyGroup, setBusyGroup] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['reorder'],
    queryFn: () => api.get<{ groups: Group[]; total: number }>('/api/stock/reorder'),
  });

  const createOrder = useMutation({
    mutationFn: (group: Group) =>
      api.post<{ document: { id: string } }>('/api/documents', {
        type: 'purchase_order',
        partnerId: group.supplierId,
        documentDate: today(),
        subject: 'Bestellung aus Bestellvorschlag',
        lines: group.items.map((item, index) => ({
          position: (index + 1) * 10,
          lineType: 'item',
          itemId: item.id,
          description: item.name,
          quantity: item.suggestedQuantity,
          unit: item.unit,
          unitPrice: item.purchasePrice,
          discountPercent: 0,
          taxCode: 'USt19',
        })),
      }),
    onSuccess: (result) => {
      toast.success('Die Bestellung wurde als Entwurf angelegt.');
      navigate(`/einkauf/bestellungen/${result.document.id}`);
    },
    onError: (error) => {
      setBusyGroup(null);
      toast.error(error instanceof ApiError ? error.message : 'Die Bestellung konnte nicht angelegt werden.');
    },
  });

  if (isLoading) return <LoadingBlock />;

  return (
    <>
      <PageHeader
        title="Bestellvorschlag"
        subtitle={
          data ? `${data.total} Artikel unter dem Meldebestand` : undefined
        }
      />

      {data && data.groups.length === 0 ? (
        <Alert kind="success">
          Alle bestandsgeführten Artikel liegen über ihrem Meldebestand. Es ist nichts zu bestellen.
        </Alert>
      ) : null}

      {data?.groups.map((group) => (
        <div className="card" key={group.supplierId ?? 'ohne'}>
          <div className="card-header">
            <h2>{group.supplierName ?? 'Ohne hinterlegten Lieferanten'}</h2>
            {group.supplierId && can('purchase:write') ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busyGroup !== null}
                onClick={() => {
                  setBusyGroup(group.supplierId);
                  createOrder.mutate(group);
                }}
              >
                Bestellung erzeugen
              </button>
            ) : (
              <span className="faint">
                Kein Lieferant am Artikel hinterlegt — bitte im Artikelstamm ergänzen.
              </span>
            )}
          </div>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Nummer</th>
                  <th>Bezeichnung</th>
                  <th className="shrink">Lieferanten-Nr.</th>
                  <th className="numeric">Bestand</th>
                  <th className="numeric">Meldebestand</th>
                  <th className="numeric">Vorschlag</th>
                  <th className="numeric">EK</th>
                  <th className="shrink">Lieferzeit</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => (
                  <tr key={item.id}>
                    <td className="mono">{item.number}</td>
                    <td>{item.name}</td>
                    <td className="mono faint">{item.supplierItemNumber ?? ''}</td>
                    <td className="numeric" style={{ color: 'var(--warning)', fontWeight: 600 }}>
                      {formatQuantity(item.stockQuantity)}
                    </td>
                    <td className="numeric muted">{formatQuantity(item.minStock)}</td>
                    <td className="numeric" style={{ fontWeight: 600 }}>
                      {formatQuantity(item.suggestedQuantity)}
                    </td>
                    <td className="numeric muted">{formatPrice(item.purchasePrice)}</td>
                    <td className="faint">
                      {item.leadTimeDays > 0 ? `${item.leadTimeDays} Tage` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {data && data.groups.length > 0 ? (
        <p className="faint" style={{ fontSize: 12.5, marginTop: 12 }}>
          Die Vorschlagsmenge füllt den Meldebestand wieder auf und wird auf die am Artikel
          hinterlegte Bestellmenge aufgerundet.
        </p>
      ) : null}
    </>
  );
}
